import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components';
import { useApiProblem, type WholeFlatLinkPreviewResponse } from '@/shared/api';
import { WholeFlatConflictList } from './WholeFlatConflictList';

interface Props {
  preview: UseQueryResult<WholeFlatLinkPreviewResponse>;
  roomName: string;
  wholeFlatName: string;
  saving: boolean;
  failure: string | null;
  onLink: () => void;
}

/** Das Ergebnis der Serverprüfung und — nur wenn sie nichts findet — die Aktion. */
export function WholeFlatPreviewResult({
  preview,
  roomName,
  wholeFlatName,
  saving,
  failure,
  onLink,
}: Props) {
  const problem = useApiProblem(preview.error);
  const failureLine = failure && (
    <p role="alert" className="text-sealwax mt-3 text-sm">
      {failure}
    </p>
  );

  if (preview.isError) {
    return (
      <div className="mt-4" role="alert">
        <p className="text-sealwax text-sm">
          {problem?.detail ?? 'Die Prüfung ist fehlgeschlagen; es wurde nichts gespeichert.'}
        </p>
        <Button
          type="button"
          variant="ghost"
          className="mt-2 min-h-11"
          onClick={() => void preview.refetch()}
        >
          Erneut prüfen
        </Button>
      </div>
    );
  }
  if (!preview.data) {
    return (
      <p className="text-ink-soft mt-4 flex items-center gap-2 text-sm" aria-live="polite">
        <Loader2 size={14} className="animate-spin" aria-hidden />
        Prüfe Buchungen und Sperren ab heute …
      </p>
    );
  }
  if (!preview.data.canLink) {
    return (
      <div className="mt-4" aria-live="polite">
        <p className="text-sealwax flex items-center gap-2 text-sm font-semibold">
          <AlertTriangle size={16} aria-hidden />
          Überschneidung ab heute — Verknüpfen nicht möglich
        </p>
        <WholeFlatConflictList conflicts={preview.data.conflicts} />
        <p className="text-ink-soft mt-3 text-sm">
          Verknüpfen geht erst, wenn sich keine Buchung oder Sperre der beiden Apartments mehr
          überschneidet.
        </p>
        {failureLine}
      </div>
    );
  }
  return (
    <div className="mt-4" aria-live="polite">
      <p className="text-ink text-sm">
        Keine Überschneidung ab heute. Danach schließt eine Buchung oder Sperre von „{roomName}“ die
        Gesamtwohnung „{wholeFlatName}“ und umgekehrt; die Verfügbarkeit beider wird neu abgeleitet.
      </p>
      <Button
        type="button"
        variant="brand"
        className="mt-3 min-h-11"
        loading={saving}
        onClick={onLink}
      >
        Verknüpfen
      </Button>
      {failureLine}
    </div>
  );
}
